import {
    DOCUMENT_TITLE_CONTEXT,
    useDocumentTitle,
} from '@thoughtspot/blink-context';
import { cleanHTMLTags } from '@thoughtspot/browser-util';
import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import { useTranslation } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { clearQueue } from '@thoughtspot/viz-render-queue';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { useAppContext } from '/@contexts/global-app-context';
import { useLoadAnswerSessionMutation } from '/@services/answer/answer-service';
import {
    AnswerContextType,
    SageSearchResponse,
} from '/@services/generated/graphql-types';
import { getProductName } from '/@services/system/config-service/session-service';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { useRuntimeParams } from '/@utils/runtime-filters.util';
import {
    getAccessTypeForObject,
    getCommonAnswerPageProperties,
} from '/@utils/viz/metrics-event.util';
import { NavigationModalContainerProps } from '../components/navigation-blocker/navigation-blocker-modal.container';
import { BaseAnswerLayoutContextValue } from './base-answer-layout-context';

const logger = create('saved-answer-layout');

export interface SavedAnswerLayoutContextValue
    extends BaseAnswerLayoutContextValue {
    navModalProps: NavigationModalContainerProps;
}

const SavedAnswerLayoutContext = createContext<SavedAnswerLayoutContextValue | null>(
    null,
);
SavedAnswerLayoutContext.displayName = 'SavedAnswerLayoutContext';

const SavedAnswerLayoutProviderInner: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { answerId = '' } = useParams<{ answerId: string }>();
    const { t } = useTranslation();
    const runtimeParams = useRuntimeParams();
    const { metricsService } = useAppContext();
    const companyTitle = getProductName();

    const [answerSessionId, setAnswerSessionId] = useState('');
    const [answerPageProperties, setAnswerPageProperties] = useState(null);

    // Persisted across the Answer<->Spreadsheet child-route remount (this
    // provider stays mounted). Lets the Answer view restore sage tokens and
    // skip auto-focus when returning from the Spreadsheet.
    const [
        cachedSageQueryState,
        setCachedSageQueryState,
    ] = useState<SageSearchResponse | null>(null);
    const [hasVisitedSpreadsheet, setHasVisitedSpreadsheet] = useState(false);
    const markSpreadsheetVisited = useCallback(
        () => setHasVisitedSpreadsheet(true),
        [],
    );

    const { setDocumentTitle } = useDocumentTitle({
        companyTitle,
    } as DOCUMENT_TITLE_CONTEXT);

    const onLoadSessionComplete = (
        sessionId: string,
        pinboardName?: string,
    ) => {
        setAnswerSessionId(sessionId);
        setDocumentTitle?.(cleanHTMLTags(pinboardName));
    };

    const { loadAnswer, data, loading, error } = useLoadAnswerSessionMutation(
        answerId,
        onLoadSessionComplete,
        false,
        {
            isClientPaginated: true,
            size: envFlags.getValue(FLAGS.dataBatchSize?.name) || 1000,
            offset: 0,
        },
        AnswerContextType.SavedAnswer,
        ['*'],
    );

    const navModalProps: NavigationModalContainerProps = {
        title: t('answer.unsavedChangesAlert.title'),
        navServicePromptMessage: t('answer.unsavedChangesAlert.message'),
        confirmButton: {},
        denyButton: {},
        dismissButton: {},
        // Allow free navigation between Answer and Spreadsheet (sibling routes
        // under the same saved-answer/:answerId). They share session state so no
        // data is lost in transit — only block transitions that leave the context.
        shouldBlockRouteTransition: ({ currentLocation, nextLocation }) => {
            const strip = (p: string) => p.replace(/\/spreadsheet\/?$/, '');
            const base = strip(currentLocation.pathname);
            const nextBase = strip(nextLocation.pathname);
            return base !== nextBase;
        },
    };

    const getAnswer = () => {
        clearQueue();
        loadAnswer()
            .then(response => {
                const {
                    data: { Answer__load: answerSessionData },
                } = response;
                const props = getCommonAnswerPageProperties(answerSessionData);
                return setAnswerPageProperties(props);
            })
            .catch(() => {
                metricsService.collectEvent(
                    new UserAction(userActionTypeEnum.LOAD_ANSWER_FAILURE),
                    {
                        answerGuid: answerId,
                        answerContext: AnswerContextType.SavedAnswer,
                    },
                );
                logger.error('Error in loading answer');
            });
    };

    useEffect(() => {
        getAnswer();
    }, [answerId, runtimeParams]);

    useUpdateEffect(() => {
        if (data && answerPageProperties) {
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.LOAD_ANSWER),
                {
                    chartType: answerPageProperties.vizChartType,
                    kpiChartType: answerPageProperties.kpiChartType,
                    objectId: answerPageProperties.objectId,
                    accessType: getAccessTypeForObject(data.permission),
                },
            );
        }
    }, [data, answerPageProperties]);

    return (
        <SavedAnswerLayoutContext.Provider
            value={{
                answerId,
                answerSessionId,
                data,
                loading,
                error,
                navModalProps,
                cachedSageQueryState,
                setCachedSageQueryState,
                hasVisitedSpreadsheet,
                markSpreadsheetVisited,
            }}
        >
            {children}
        </SavedAnswerLayoutContext.Provider>
    );
};

export const SavedAnswerLayoutProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    // In embedded app contexts (full-app embed), skip the layout provider so
    // SavedAnswerPage falls back to standalone mode. Spreadsheet and the shared
    // data-loading logic are not supported in embedded contexts.
    if (isEmbeddedApp()) {
        return <>{children}</>;
    }
    return (
        <SavedAnswerLayoutProviderInner>
            {children}
        </SavedAnswerLayoutProviderInner>
    );
};

export const useSavedAnswerLayoutContext = (): SavedAnswerLayoutContextValue => {
    const ctx = useContext(SavedAnswerLayoutContext);
    if (ctx === null) {
        throw new Error(
            'useSavedAnswerLayoutContext must be used within SavedAnswerLayoutProvider',
        );
    }
    return ctx;
};

/**
 * Returns the layout context when available, or null when SavedAnswerPage is
 * rendered standalone (without SavedAnswerLayoutProvider as an ancestor).
 *
 * Backward-compatibility shim — callers that do not yet wrap SavedAnswerPage
 * in SavedAnswerLayoutProvider will get null here and fall back to their own
 * loading logic.
 *
 * TODO: once every callsite is migrated to use SavedAnswerLayoutProvider,
 * remove this hook and replace usages with useSavedAnswerLayoutContext.
 */
export const useOptionalSavedAnswerLayoutContext = (): SavedAnswerLayoutContextValue | null =>
    useContext(SavedAnswerLayoutContext);
